import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Switch } from "./ui/switch";
import {
  Clock,
  Package,
  DollarSign,
  Users,
  ShoppingCart,
  Edit,
  Trash2,
  PackagePlus,
} from "lucide-react";

interface PackageType {
  id: number;
  name: string;
  type: string;
  price: number;
  duration: string;
  hours: number | "unlimited";
  description: string;
  popular: boolean;
}

interface Purchase {
  id: number;
  clientName: string;
  packageName: string;
  packageType: string;
  price: number;
  purchaseDate: string;
  status: string;
}

export function PackageManagement() {
  const [packages, setPackages] = useState<PackageType[]>([
    {
      id: 1,
      name: "Monthly Premium",
      type: "Monthly",
      price: 8800,
      duration: "30 days",
      hours: "unlimited",
      description: "Unlimited access with premium amenities and 24/7 support",
      popular: true,
    },
    {
      id: 2,
      name: "Monthly Regular",
      type: "Monthly",
      price: 6600,
      duration: "30 days",
      hours: "unlimited",
      description: "Unlimited access during business hours",
      popular: false,
    },
    {
      id: 3,
      name: "30-hour Package",
      type: "Hour Package",
      price: 19800,
      duration: "90 days",
      hours: 30,
      description: "Flexible 30 hours to use within 3 months",
      popular: false,
    },
    {
      id: 4,
      name: "10-hour Package",
      type: "Hour Package",
      price: 7425,
      duration: "30 days",
      hours: 10,
      description: "Perfect for occasional users",
      popular: false,
    },
    {
      id: 5,
      name: "Daily Pass",
      type: "Daily",
      price: 1925,
      duration: "1 day",
      hours: 8,
      description: "Full day access with all amenities",
      popular: false,
    },
    {
      id: 6,
      name: "Hourly Rate",
      type: "Hourly",
      price: 825,
      duration: "Per hour",
      hours: 1,
      description: "Pay as you go hourly rate",
      popular: false,
    },
  ]);

  const [
    purchases,
    // setPurchases
  ] = useState<Purchase[]>([
    {
      id: 1,
      clientName: "John Doe",
      packageName: "Monthly Premium",
      packageType: "Monthly",
      price: 8800,
      purchaseDate: "2024-06-15",
      status: "Active",
    },
    {
      id: 2,
      clientName: "Sarah Wilson",
      packageName: "30-hour Package",
      packageType: "Hour Package",
      price: 19800,
      purchaseDate: "2024-06-10",
      status: "Active",
    },
    {
      id: 3,
      clientName: "Mike Johnson",
      packageName: "Daily Pass",
      packageType: "Daily",
      price: 1925,
      purchaseDate: "2024-06-18",
      status: "Used",
    },
  ]);

  const [editingPackage, setEditingPackage] = useState<PackageType | null>(
    null
  );
  const [newPackage, setNewPackage] = useState<Partial<PackageType>>({
    name: "",
    type: "",
    price: 0,
    duration: "",
    hours: 0,
    description: "",
    popular: false,
  });
  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const [showEditSheet, setShowEditSheet] = useState(false);

  const packageTypes = ["Monthly", "Daily", "Hourly", "Hour Package"];

  const handleCreatePackage = () => {
    if (
      !newPackage.name ||
      !newPackage.type ||
      !newPackage.price ||
      !newPackage.duration
    )
      return;

    const packageToCreate: PackageType = {
      id: Math.max(...packages.map((p) => p.id)) + 1,
      name: newPackage.name!,
      type: newPackage.type!,
      price: newPackage.price!,
      duration: newPackage.duration!,
      hours: newPackage.hours || 0,
      description: newPackage.description || "",
      popular: newPackage.popular || false,
    };

    setPackages([...packages, packageToCreate]);
    setNewPackage({
      name: "",
      type: "",
      price: 0,
      duration: "",
      hours: 0,
      description: "",
      popular: false,
    });
    setShowCreateSheet(false);
  };

  const handleEditPackage = () => {
    if (!editingPackage) return;

    const updatedPackages = packages.map((pkg) =>
      pkg.id === editingPackage.id ? editingPackage : pkg
    );
    setPackages(updatedPackages);
    setEditingPackage(null);
    setShowEditSheet(false);
  };

  const handleDeletePackage = (packageId: number) => {
    setPackages(packages.filter((pkg) => pkg.id !== packageId));
  };

  const openEditSheet = (pkg: PackageType) => {
    setEditingPackage({ ...pkg });
    setShowEditSheet(true);
  };

  const getPackageTypeColor = (type: string) => {
    switch (type) {
      case "Monthly":
        return "bg-blue-100 text-blue-800";
      case "Daily":
        return "bg-green-100 text-green-800";
      case "Hourly":
        return "bg-yellow-100 text-yellow-800";
      case "Hour Package":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-800";
      case "Used":
        return "bg-gray-100 text-gray-800";
      case "Expired":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const totalRevenue = purchases.reduce(
    (sum, purchase) => sum + purchase.price,
    0
  );
  const activePackages = purchases.filter((p) => p.status === "Active").length;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Total Packages</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{packages.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Active Subscriptions</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{activePackages}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">₱{totalRevenue.toLocaleString()}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">This Month</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{purchases.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Available Packages */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle>Available Packages</CardTitle>
              <CardDescription>
                Manage workspace packages and pricing
              </CardDescription>
            </div>
            <Sheet open={showCreateSheet} onOpenChange={setShowCreateSheet}>
              <SheetTrigger asChild>
                <Button variant="outline">
                  <PackagePlus className="mr-2 h-4 w-4" />
                  Create Package
                </Button>
              </SheetTrigger>
              <SheetContent className="w-[400px] sm:w-[450px]">
                <SheetHeader className="space-y-3 px-6">
                  <SheetTitle>Create New Package</SheetTitle>
                  <SheetDescription>
                    Add a new workspace package to your offerings
                  </SheetDescription>
                </SheetHeader>

                <div className="flex flex-col h-[calc(100vh-120px)] px-6">
                  <div className="flex-1 space-y-8 overflow-y-auto py-8">
                    {/* Basic Information Section */}
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label
                          htmlFor="new-name"
                          className="text-sm font-medium"
                        >
                          Package Name
                        </Label>
                        <Input
                          id="new-name"
                          value={newPackage.name || ""}
                          onChange={(e) =>
                            setNewPackage({
                              ...newPackage,
                              name: e.target.value,
                            })
                          }
                          placeholder="e.g., Premium Monthly"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="new-type"
                          className="text-sm font-medium"
                        >
                          Package Type
                        </Label>
                        <Select
                          value={newPackage.type || ""}
                          onValueChange={(value) =>
                            setNewPackage({ ...newPackage, type: value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {packageTypes.map((type) => (
                              <SelectItem key={type} value={type}>
                                {type}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Pricing & Duration Section */}
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label
                            htmlFor="new-price"
                            className="text-sm font-medium"
                          >
                            Price (₱)
                          </Label>
                          <Input
                            id="new-price"
                            type="number"
                            value={newPackage.price || ""}
                            onChange={(e) =>
                              setNewPackage({
                                ...newPackage,
                                price: parseInt(e.target.value) || 0,
                              })
                            }
                            placeholder="0"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label
                            htmlFor="new-hours"
                            className="text-sm font-medium"
                          >
                            Hours
                          </Label>
                          <Input
                            id="new-hours"
                            value={
                              newPackage.hours === "unlimited"
                                ? "unlimited"
                                : newPackage.hours || ""
                            }
                            onChange={(e) =>
                              setNewPackage({
                                ...newPackage,
                                hours:
                                  e.target.value === "unlimited"
                                    ? "unlimited"
                                    : parseInt(e.target.value) || 0,
                              })
                            }
                            placeholder="unlimited or number"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="new-duration"
                          className="text-sm font-medium"
                        >
                          Duration
                        </Label>
                        <Input
                          id="new-duration"
                          value={newPackage.duration || ""}
                          onChange={(e) =>
                            setNewPackage({
                              ...newPackage,
                              duration: e.target.value,
                            })
                          }
                          placeholder="e.g., 30 days"
                        />
                      </div>
                    </div>

                    {/* Description Section */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="new-description"
                        className="text-sm font-medium"
                      >
                        Description
                      </Label>
                      <Textarea
                        id="new-description"
                        value={newPackage.description || ""}
                        onChange={(e) =>
                          setNewPackage({
                            ...newPackage,
                            description: e.target.value,
                          })
                        }
                        placeholder="Package description..."
                        rows={4}
                        className="resize-none"
                      />
                    </div>

                    {/* Settings Section */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between space-x-3 rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <Label
                            htmlFor="new-popular"
                            className="text-sm font-medium"
                          >
                            Popular Package
                          </Label>
                          <div className="text-sm text-muted-foreground">
                            Mark this package as popular to highlight it
                          </div>
                        </div>
                        <Switch
                          id="new-popular"
                          checked={newPackage.popular || false}
                          onCheckedChange={(checked) =>
                            setNewPackage({ ...newPackage, popular: checked })
                          }
                        />
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons - Fixed at bottom with better spacing */}
                  <div className="flex gap-3 pt-8 mt-8 border-t">
                    <Button
                      onClick={handleCreatePackage}
                      className="flex-1"
                      disabled={
                        !newPackage.name ||
                        !newPackage.type ||
                        !newPackage.price ||
                        !newPackage.duration
                      }
                    >
                      Create Package
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowCreateSheet(false)}
                      className="px-6"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Package Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Hours</TableHead>
                <TableHead>Popular</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {packages.map((pkg) => (
                <TableRow key={pkg.id}>
                  <TableCell>
                    <div className="font-medium">{pkg.name}</div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getPackageTypeColor(pkg.type)}>
                      {pkg.type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">
                      ₱{pkg.price.toLocaleString()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-muted-foreground">
                      {pkg.duration}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span className="text-sm">
                        {pkg.hours === "unlimited"
                          ? "Unlimited"
                          : `${pkg.hours}h`}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {pkg.popular && (
                      <Badge className="bg-amber-100 text-amber-800">
                        Popular
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-muted-foreground max-w-xs truncate">
                      {pkg.description}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditSheet(pkg)}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" size="sm">
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Package</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{pkg.name}"? This
                              action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeletePackage(pkg.id)}
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Package Sheet */}
      <Sheet open={showEditSheet} onOpenChange={setShowEditSheet}>
        <SheetContent className="w-[400px] sm:w-[450px]">
          <SheetHeader className="space-y-3 px-6">
            <SheetTitle>Edit Package</SheetTitle>
            <SheetDescription>
              Update package information and settings
            </SheetDescription>
          </SheetHeader>

          {editingPackage && (
            <div className="flex flex-col h-[calc(100vh-120px)] px-6">
              <div className="flex-1 space-y-8 overflow-y-auto py-8">
                {/* Basic Information Section */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-name" className="text-sm font-medium">
                      Package Name
                    </Label>
                    <Input
                      id="edit-name"
                      value={editingPackage.name}
                      onChange={(e) =>
                        setEditingPackage({
                          ...editingPackage,
                          name: e.target.value,
                        })
                      }
                      placeholder="e.g., Premium Monthly"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit-type" className="text-sm font-medium">
                      Package Type
                    </Label>
                    <Select
                      value={editingPackage.type}
                      onValueChange={(value) =>
                        setEditingPackage({ ...editingPackage, type: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {packageTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Pricing & Duration Section */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label
                        htmlFor="edit-price"
                        className="text-sm font-medium"
                      >
                        Price (₱)
                      </Label>
                      <Input
                        id="edit-price"
                        type="number"
                        value={editingPackage.price}
                        onChange={(e) =>
                          setEditingPackage({
                            ...editingPackage,
                            price: parseInt(e.target.value) || 0,
                          })
                        }
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="edit-hours"
                        className="text-sm font-medium"
                      >
                        Hours
                      </Label>
                      <Input
                        id="edit-hours"
                        value={
                          editingPackage.hours === "unlimited"
                            ? "unlimited"
                            : editingPackage.hours
                        }
                        onChange={(e) =>
                          setEditingPackage({
                            ...editingPackage,
                            hours:
                              e.target.value === "unlimited"
                                ? "unlimited"
                                : parseInt(e.target.value) || 0,
                          })
                        }
                        placeholder="unlimited or number"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="edit-duration"
                      className="text-sm font-medium"
                    >
                      Duration
                    </Label>
                    <Input
                      id="edit-duration"
                      value={editingPackage.duration}
                      onChange={(e) =>
                        setEditingPackage({
                          ...editingPackage,
                          duration: e.target.value,
                        })
                      }
                      placeholder="e.g., 30 days"
                    />
                  </div>
                </div>

                {/* Description Section */}
                <div className="space-y-2">
                  <Label
                    htmlFor="edit-description"
                    className="text-sm font-medium"
                  >
                    Description
                  </Label>
                  <Textarea
                    id="edit-description"
                    value={editingPackage.description}
                    onChange={(e) =>
                      setEditingPackage({
                        ...editingPackage,
                        description: e.target.value,
                      })
                    }
                    placeholder="Package description..."
                    rows={4}
                    className="resize-none"
                  />
                </div>

                {/* Settings Section */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between space-x-3 rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <Label
                        htmlFor="edit-popular"
                        className="text-sm font-medium"
                      >
                        Popular Package
                      </Label>
                      <div className="text-sm text-muted-foreground">
                        Mark this package as popular to highlight it
                      </div>
                    </div>
                    <Switch
                      id="edit-popular"
                      checked={editingPackage.popular}
                      onCheckedChange={(checked) =>
                        setEditingPackage({
                          ...editingPackage,
                          popular: checked,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons - Fixed at bottom with better spacing */}
              <div className="flex gap-3 pt-8 mt-8 border-t">
                <Button
                  onClick={handleEditPackage}
                  className="flex-1"
                  disabled={
                    !editingPackage.name ||
                    !editingPackage.type ||
                    !editingPackage.price ||
                    !editingPackage.duration
                  }
                >
                  Update Package
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowEditSheet(false)}
                  className="px-6"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Recent Purchases */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Purchases</CardTitle>
          <CardDescription>
            Latest package purchases and subscriptions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Package</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {purchases.map((purchase) => (
                <TableRow key={purchase.id}>
                  <TableCell>{purchase.clientName}</TableCell>
                  <TableCell>{purchase.packageName}</TableCell>
                  <TableCell>
                    <Badge
                      className={getPackageTypeColor(purchase.packageType)}
                    >
                      {purchase.packageType}
                    </Badge>
                  </TableCell>
                  <TableCell>₱{purchase.price.toLocaleString()}</TableCell>
                  <TableCell>{purchase.purchaseDate}</TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(purchase.status)}>
                      {purchase.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
